import WebComponent from '../../WebComponent.js'

export default class Turnstile extends WebComponent {

  constructor() {
    super()
    this.siteKey = ''
    this.token = null
    this.widgetId = null
    this._pendingRefresh = null
    this._refreshChain = null
    // Use crypto.randomUUID() for secure unique ID generation
    this.containerId = `turnstile-${crypto.randomUUID()}`
  }

  connectedCallback() {
    super.connectedCallback()

    // Render directly into light DOM (Turnstile doesn't support Shadow DOM)
    this.innerHTML = `
      <style>
        .turnstile-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .turnstile-info {
          font-size: 0.875rem;
          color: #666;
        }
        .turnstile-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          background: #f9f9f9;
          border-radius: 4px;
          font-size: 0.75rem;
        }
      </style>
      <div class="turnstile-wrapper">
        <div id="${this.containerId}"></div>
      </div>
    `

    // Now load Turnstile
    this.loadTurnstile()
  }

  async loadTurnstile() {
    // Get site key from config
    this.siteKey = window.eformConfig?.turnstileSiteKey

    if (!this.siteKey) {
      this.innerHTML = '<div style="padding: 1rem; background: #fee; color: #c00; border-radius: 4px;">Turnstile site key not configured in config.json</div>'
      return
    }

    // Load Turnstile script
    if (!window.turnstile) {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      document.head.appendChild(script)

      // Wait for script to load
      await new Promise((resolve, reject) => {
        script.onload = resolve
        script.onerror = reject
        setTimeout(() => reject(new Error('Timeout')), 10000)
      }).catch(() => {
        this.innerHTML = '<div style="padding: 1rem; background: #fee; color: #c00; border-radius: 4px;">Failed to load Turnstile script</div>'
      })
    }

    // Render Turnstile widget
    this.renderTurnstile()
  }

  renderTurnstile() {
    const container = document.getElementById(this.containerId)
    // Retry until both Turnstile is loaded and the container is in the DOM
    if (!window.turnstile || !container) {
      setTimeout(() => this.renderTurnstile(), 100)
      return
    }

    try {
      this.widgetId = window.turnstile.render(container, {
        sitekey: this.siteKey,
        callback: token => {
          this.token = token
          this._settleRefresh(token)
          this.dispatchEvent(new CustomEvent('captcha-token', {
            detail: {token},
            bubbles: true,
            composed: true
          }))
        },
        'expired-callback': () => {
          this.token = null
        },
        'error-callback': () => {
          this._settleRefresh(null, new Error('Turnstile error'))
        },
        theme: 'light',
        size: 'normal'
      })
    } catch {
    }
  }

  executeRefresh(timeoutMs = 40000) {
    const previous = this._refreshChain ?? Promise.resolve()
    this._refreshChain = previous
      .catch(() => {}) // a failed refresh must not break the chain for the next one
      .then(() => this._requestToken(timeoutMs))
    return this._refreshChain
  }

  _requestToken(timeoutMs) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pendingRefresh = null
        reject(new Error('Turnstile token refresh timed out'))
      }, timeoutMs)
      this._pendingRefresh = {resolve, reject, timer}

      // If the widget is ready, reset it to force a new token.
      // If it hasn't rendered yet, its first callback will resolve this promise.
      if (this.widgetId !== null && window.turnstile) {
        this.token = null
        window.turnstile.reset(this.widgetId)
      }
    })
  }

  _settleRefresh(token, error = null) {
    if (!this._pendingRefresh) {
      return
    }
    clearTimeout(this._pendingRefresh.timer)
    const {resolve, reject} = this._pendingRefresh
    this._pendingRefresh = null
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  }

}

customElements.define('eform-turnstile', Turnstile)
